import React, {
	memo,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import cn from 'classnames';

import { SpinnerOverlay, Tooltip } from '@components/common';

import { useOnClickOutside } from '@hooks/utils/useOnClickOutSide';

import { IModalProps } from '@components/common/Modal/interface';

import { MODAL_WIDTH_480 } from '@constants/modals';
import { MODAL_CLOSE_BUTTON_AND_PADDING_WIDTH, modalCloseConfirm } from './constants';

import CloseButton from '../CloseButton';
import Confirm from '../Confirm';
import { modalStack, registerModal, unregisterModal } from './modalStack';

import styles from './Modal.module.scss';

export const ModalContext = React.createContext<{
	onClose: () => void;
	setIsConfirmDialogVisible: React.Dispatch<React.SetStateAction<boolean>>;
	setIsClickOutsideDisabled: React.Dispatch<React.SetStateAction<boolean>>;
	setTitleValue: React.Dispatch<React.SetStateAction<string>>;
	isDirtyForm?: boolean;
}>({
	onClose: () => {},
	setIsConfirmDialogVisible: () => {},
	setIsClickOutsideDisabled: () => {},
	setTitleValue: () => {},
	isDirtyForm: false,
});

const Modal: React.FC<IModalProps> = ({
	children,
	className,
	onClose,
	isCloseButtonHidden = false,
	isCloseOnBackdropClickNeeded,
	isConfirmDialogNeededOnClose,
	isDirtyForm,
	isLoading,
	isOpen,
	isScrollableContent,
	isTitleHidden = false,
	cancelButtonText,
	confirmButtonText,
	confirmQuestion = modalCloseConfirm.question,
	title = 'Confirm action',
	height,
	maxHeight,
	maxWidth,
	minWidth,
	width = MODAL_WIDTH_480,
}) => {
	const modalRef = useRef<HTMLDivElement>(null);
	const titleRef = useRef<HTMLDivElement>(null);

	const [isClickOutsideDisabled, setIsClickOutsideDisabled] = useState(true);
	const [isTitleFullyVisible, setIsTitleFullyVisible] = useState(false);
	const [isConfirmDialogVisible, setIsConfirmDialogVisible] = useState(false);
	const [titleValue, setTitleValue] = useState(title);

	const checkTitleFit = useCallback(() => {
		if (titleRef.current && modalRef.current) {
			const TITLE_WRAPPER_WIDTH = modalRef.current?.clientWidth - MODAL_CLOSE_BUTTON_AND_PADDING_WIDTH;

			const isFullSize = titleRef.current?.scrollWidth <= TITLE_WRAPPER_WIDTH;

			setIsTitleFullyVisible(isFullSize);
		}
	}, []);

	useLayoutEffect(() => {
		checkTitleFit();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [titleValue, titleRef.current]);

	useLayoutEffect(() => {
		if (modalRef.current) {
			modalRef.current.focus();
		}
	}, []);

	useEffect(() => {
		setTitleValue(title);
	}, [title]);

	const modalContainerClasses = cn(styles.modalOverlay, className);

	const modalClassname = useMemo(() => cn(styles.modal, {
		[styles.modalScrollable]: isScrollableContent,
		[styles.modalFullHeight]: height,
	}), [isScrollableContent, height]);

	const contentClassname = useMemo(() => cn(styles.modalContent, {
		[styles.modalWithScrollableContent]: isScrollableContent,
		[styles.modalWithoutTitle]: !titleValue || isTitleHidden,
	}), [isScrollableContent, titleValue, isTitleHidden]);

	const closeConfirmDialog = useCallback(() => setIsConfirmDialogVisible(false), []);

	const handleClose = useCallback((closeWithoutSaving: boolean) => {
		if (isConfirmDialogNeededOnClose && isDirtyForm && !closeWithoutSaving) {
			setIsConfirmDialogVisible(true);
		} else {
			closeConfirmDialog();
			onClose();
		}
	}, [closeConfirmDialog, isConfirmDialogNeededOnClose, isDirtyForm, onClose]);

	const handleClickOutside = useCallback(() => {
		handleClose(true);
	}, [handleClose]);

	useOnClickOutside({
		refs: [modalRef],
		handler: handleClickOutside,
		disableClickOutside: isClickOutsideDisabled || !isCloseOnBackdropClickNeeded,
	});

	const confirmCloseWithoutSaving = useCallback(() => handleClose(true), [handleClose]);

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			const isTopModal = modalStack[modalStack.length - 1] === onClose;

			if (!isTopModal) return;

			if (event.key === 'Escape') {
				modalStack.pop();
				onClose();
			}

			if (event.key === 'Enter') {
				if (isConfirmDialogVisible) {
					confirmCloseWithoutSaving();
				}
			}
		};

		window.addEventListener('keydown', handleKeyDown);

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [onClose, isConfirmDialogVisible, confirmCloseWithoutSaving]);

	const handleCloseClick = useCallback(() => {
		handleClose(false);
	}, [handleClose]);

	const contextValue = useMemo(() => ({
		onClose: handleCloseClick,
		setIsConfirmDialogVisible,
		setIsClickOutsideDisabled,
		isDirtyForm,
		setTitleValue,
	}), [handleCloseClick, setIsConfirmDialogVisible, setIsClickOutsideDisabled, isDirtyForm]);

	useEffect(() => {
		registerModal(onClose);

		return () => {
			unregisterModal(onClose);
		};
	}, [onClose]);

	const handleBackdropClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
		if (e.target === e.currentTarget && isCloseOnBackdropClickNeeded) {
			onClose();
		}
	}, [isCloseOnBackdropClickNeeded, onClose]);

	if (!isOpen) {
		return null;
	}

	return (
		<div
			className={modalContainerClasses}
			data-testid='modal-container'
			onClick={handleBackdropClick}
		>
			<div
				ref={modalRef}
				className={modalClassname}
				role='dialog'
				style={{ width, minWidth, height, maxWidth, maxHeight }}
				tabIndex={-1}
			>
				{isLoading && <SpinnerOverlay />}
				{titleValue && !isTitleHidden && (
					<div className={styles.modalHeader}>
						<Tooltip
							content={titleValue}
							isDisabled={isTitleFullyVisible}
							placement='top'
						>
							<div
								ref={titleRef}
								className={styles.modalTitle}
							>
								{titleValue}
							</div>
						</Tooltip>
						{!isCloseButtonHidden &&
							<CloseButton
								className={styles.modalCloseIcon}
								onClick={handleCloseClick}
							/>
						}
					</div>
				)}
				<div className={contentClassname}>
					<div className={styles.modalWrapper}>
						<ModalContext.Provider value={contextValue}>
							{children}
						</ModalContext.Provider>
						{(!titleValue || isTitleHidden) && !isCloseButtonHidden && (
							<CloseButton
								className={styles.modalCloseIcon}
								onClick={onClose}
							/>
						)}
					</div>
				</div>
			</div>

			<Modal
				isOpen={isConfirmDialogVisible}
				onClose={closeConfirmDialog}
				title={titleValue}
				width='fit-content'
			>
				<Confirm
					negativeBtnText={cancelButtonText}
					onClose={closeConfirmDialog}
					onSubmit={confirmCloseWithoutSaving}
					positiveBtnText={confirmButtonText}
					question={confirmQuestion}
				>
					{modalCloseConfirm.children}
				</Confirm>
			</Modal>
		</div>
	);
};

export default memo(Modal);
